'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { getChatCompletion } from '@/lib/ai/chatCompletion';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface FeedItem {
  id: string;
  type: 'service' | 'giving' | 'caretaking' | 'khidmah' | 'islah';
  title: string;
  description: string;
  city?: string;
  ward?: string;
  region?: string;
  domain: string;
  impact_tags: string[];
  contributor?: string;
  contributor_role?: string;
  contributor_badge?: string;
  timestamp: string;
  source?: string;
  source_url?: string;
  name_of_allah?: string;
  ayat_ref?: string;
  is_followed?: boolean;
  reaction_count?: number;
  is_verified?: boolean;
  partner_network?: string;
}

interface FeedFilters {
  city: string;
  domain: string;
  type: string;
  sort: 'recent' | 'impact' | 'city';
}

// ─── IMPACT TAG CONFIG ────────────────────────────────────────────────────────

const IMPACT_TAG_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  'Islah': { bg: 'rgba(45,122,71,0.12)', border: 'rgba(45,122,71,0.35)', text: '#2d7a47' },
  'Khidmah': { bg: 'rgba(45,138,135,0.12)', border: 'rgba(45,138,135,0.35)', text: '#2d8a87' },
  'Amanah': { bg: 'rgba(201,168,76,0.12)', border: 'rgba(201,168,76,0.35)', text: '#c9a84c' },
  'Mizan': { bg: 'rgba(138,138,184,0.12)', border: 'rgba(138,138,184,0.35)', text: '#8a8ab8' },
  'Fisabilillah': { bg: 'rgba(212,168,42,0.12)', border: 'rgba(212,168,42,0.35)', text: '#D4A82A' },
  'Justice': { bg: 'rgba(180,80,80,0.12)', border: 'rgba(180,80,80,0.35)', text: '#c05050' },
  'Ecology': { bg: 'rgba(45,122,71,0.12)', border: 'rgba(45,122,71,0.35)', text: '#2d7a47' },
  'Education': { bg: 'rgba(45,138,135,0.12)', border: 'rgba(45,138,135,0.35)', text: '#2d8a87' },
  'Housing': { bg: 'rgba(138,100,60,0.12)', border: 'rgba(138,100,60,0.35)', text: '#8a6a3a' },
  'Food': { bg: 'rgba(80,140,80,0.12)', border: 'rgba(80,140,80,0.35)', text: '#508050' },
  'Health': { bg: 'rgba(80,100,180,0.12)', border: 'rgba(80,100,180,0.35)', text: '#5064b4' },
  'Reentry': { bg: 'rgba(180,120,40,0.12)', border: 'rgba(180,120,40,0.35)', text: '#b47828' },
};

// ─── VERIFIED PARTNER ORG DATA ────────────────────────────────────────────────
// All URLs verified against official organization websites as of 2026
// MAS: muslimamericansociety.org | MANA: manajustice.org
// CIOGC: ciogc.org | Zakat Foundation: zakatfoundation.org
// Chi-Care: chi-care.org | ICNA Relief: icnarelief.org

interface ExternalEvent {
  id: string;
  title: string;
  description: string;
  city?: string;
  domain?: string;
  date?: string;
  url?: string;
  source: string;
  partner: 'CIOGC' | 'Zakat Foundation' | 'MAS' | 'Chi-Care' | 'MANA' | 'ICNA';
}

const PARTNER_EVENTS: ExternalEvent[] = [
  {
    id: 'ciogc-1',
    title: 'CIOGC Asbab Quest: Civic Engagement & Voter Registration Drive',
    description: 'The Council of Islamic Organizations of Greater Chicago (CIOGC) runs ongoing civic engagement initiatives including voter registration, candidate forums, and community advocacy. Asbab Quest: Register 5 community members to vote or attend a CIOGC civic forum. Verified source: ciogc.org',
    city: 'Chicago',
    domain: 'Justice & Civic',
    date: '2026-04-01T10:00:00Z',
    url: 'https://www.ciogc.org',
    source: 'CIOGC',
    partner: 'CIOGC',
  },
  {
    id: 'ciogc-2',
    title: 'CIOGC Asbab Quest: Ramadan Food Drive — Feed Chicago Families',
    description: 'CIOGC coordinates annual Ramadan food drives across Chicago-area masajid, distributing groceries and iftar meals to families in need. Asbab Quest: Donate or volunteer at a CIOGC food drive site. Verified program — ciogc.org',
    city: 'Chicago',
    domain: 'Food Security',
    date: '2026-03-20T09:00:00Z',
    url: 'https://www.ciogc.org',
    source: 'CIOGC',
    partner: 'CIOGC',
  },
  {
    id: 'zakat-1',
    title: 'Zakat Foundation Asbab Quest: Domestic Hunger Relief Program',
    description: 'Zakat Foundation Foundation America operates a domestic hunger relief program providing food assistance to low-income families across the US. Asbab Quest: Sponsor a food basket or volunteer at a local distribution site. Verified: zakatfoundation.org/programs/domestic-hunger-relief',
    city: 'Chicago',
    domain: 'Food Security',
    date: '2026-03-15T08:00:00Z',
    url: 'https://www.zakatfoundation.org/programs/domestic-hunger-relief/',
    source: 'Zakat Foundation Foundation America',
    partner: 'Zakat Foundation',
  },
  {
    id: 'zakat-2',
    title: 'Zakat Foundation Asbab Quest: Orphan Sponsorship & Education Fund',
    description: 'Zakat Foundation\'s orphan sponsorship program provides education, healthcare, and support to orphaned children globally. Asbab Quest: Sponsor an orphan or contribute to the education fund. Verified: zakatfoundation.org/programs/orphan-sponsorship',
    city: 'National',
    domain: 'Education',
    date: '2026-03-18T10:00:00Z',
    url: 'https://www.zakatfoundation.org/programs/orphan-sponsorship/',
    source: 'Zakat Foundation Foundation America',
    partner: 'Zakat Foundation',
  },
  {
    id: 'mas-1',
    title: 'MAS Asbab Quest: Youth Leadership & Tarbiyah Program',
    description: 'Muslim American Society (MAS) runs youth tarbiyah (character development) programs and leadership training across chapters nationwide. Asbab Quest: Enroll a youth in MAS tarbiyah or volunteer as a mentor. Verified: muslimamericansociety.org',
    city: 'Chicago',
    domain: 'Education',
    date: '2026-03-22T14:00:00Z',
    url: 'https://www.muslimamericansociety.org',
    source: 'MAS',
    partner: 'MAS',
  },
  {
    id: 'mas-2',
    title: 'MAS Asbab Quest: Community Service Day — Neighborhood Islah',
    description: 'MAS chapters organize regular community service days including neighborhood cleanups, park restoration, and beautification projects rooted in the principle of Islah. Asbab Quest: Join or organize a MAS community service day in your locality. Verified: muslimamericansociety.org',
    city: 'National',
    domain: 'Community Restoration',
    date: '2026-04-05T09:00:00Z',
    url: 'https://www.muslimamericansociety.org',
    source: 'MAS',
    partner: 'MAS',
  },
  {
    id: 'chicare-1',
    title: 'Chi-Care Asbab Quest: Mobile Meal Routes — Feed the Unhoused',
    description: 'Chi-Care operates mobile meal distribution routes delivering fresh food to unhoused individuals across Chicago neighborhoods. Asbab Quest: Join a Chi-Care meal route or donate to fund a week of meals. Verified: chi-care.org',
    city: 'Chicago',
    domain: 'Food Security',
    date: '2026-03-14T08:00:00Z',
    url: 'https://chi-care.org',
    source: 'Chi-Care',
    partner: 'Chi-Care',
  },
  {
    id: 'chicare-2',
    title: 'Chi-Care Asbab Quest: Community Fridge Network — Caretaker of Provisions',
    description: 'Chi-Care supports and stocks community fridges across Chicago, providing 24/7 access to fresh food in underserved neighborhoods. Asbab Quest: Stock a community fridge, photograph the provisions as Ayat of Ar-Razzaq; The Ever-Provider, and submit an IRL Lens observation linking the act to Quran 2:177. Source: chi-care.org | Verified program.',
    city: 'Chicago',
    domain: 'Food Security',
    date: '2026-03-17T10:00:00Z',
    url: 'https://chi-care.org',
    source: 'Chi-Care',
    partner: 'Chi-Care',
  },
  {
    id: 'mana-1',
    title: 'MANA Asbab Quest: Justice & Advocacy — Upholding Rights of Black Muslims',
    description: 'The Muslim Alliance in North America (MANA) works for justice and equity, particularly for Black Muslim communities. MANA\'s advocacy work addresses systemic injustice through community organizing and policy engagement. Asbab Quest: Engage with MANA\'s justice initiatives or support their advocacy campaigns. Verified: manajustice.org',
    city: 'National',
    domain: 'Justice & Reentry',
    date: '2026-03-25T11:00:00Z',
    url: 'https://www.manajustice.org',
    source: 'MANA',
    partner: 'MANA',
  },
  {
    id: 'mana-2',
    title: 'MANA Asbab Quest: Mosque-Based Social Services — Connecting Masajid to Community Needs',
    description: 'MANA works to connect masajid with social service delivery, particularly in underserved Black Muslim communities. Asbab Quest: Map your local masjid\'s social services using the MRG framework and submit findings. Verified: manajustice.org',
    city: 'National',
    domain: 'Community Restoration',
    date: '2026-03-28T13:00:00Z',
    url: 'https://www.manajustice.org',
    source: 'MANA',
    partner: 'MANA',
  },
  {
    id: 'icna-1',
    title: 'ICNA Relief Asbab Quest: Ramadan Food Basket Program',
    description: 'ICNA Relief distributes Ramadan food baskets to thousands of low-income families across the US each year. Asbab Quest: Sponsor one food basket ($50) or volunteer at a local ICNA Relief distribution site. Reflect on Quran 76:8 — "They give food, in spite of their love for it, to the poor, the orphan, and the captive." Source: icnarelief.org | Verified 501(c)(3).',
    city: 'Chicago',
    domain: 'Food Security',
    date: '2026-03-14T08:00:00Z',
    url: 'https://www.icnarelief.org',
    source: 'ICNA Relief',
    partner: 'ICNA',
  },
  {
    id: 'icna-2',
    title: 'ICNA Relief Asbab Quest: Refugee Resettlement & Welcome Program',
    description: 'ICNA Relief\'s refugee resettlement program provides newly arrived refugees with housing, employment assistance, and community integration support. Asbab Quest: Volunteer as a cultural navigator or donate to the resettlement fund. Verified: icnarelief.org/refugee-services',
    city: 'National',
    domain: 'Community Restoration',
    date: '2026-04-02T10:00:00Z',
    url: 'https://www.icnarelief.org',
    source: 'ICNA Relief',
    partner: 'ICNA',
  },
];

const PARTNER_DOMAIN_MAP: Record<string, { impactTags: string[]; nameOfAllah: string; ayatRef: string; type: FeedItem['type'] }> = {
  'Food Security': { impactTags: ['Khidmah', 'Fisabilillah', 'Food', 'Amanah'], nameOfAllah: 'Ar-Razzaq', ayatRef: '2:177', type: 'khidmah' },
  'Ecology': { impactTags: ['Amanah', 'Ecology', 'Islah', 'Mizan'], nameOfAllah: 'Al-Khaliq', ayatRef: '41:53', type: 'caretaking' },
  'Education': { impactTags: ['Khidmah', 'Education', 'Fisabilillah', 'Amanah'], nameOfAllah: 'Al-Alim', ayatRef: '96:1', type: 'service' },
  'Justice & Reentry': { impactTags: ['Islah', 'Justice', 'Reentry', 'Mizan'], nameOfAllah: 'Al-Adl', ayatRef: '57:25', type: 'islah' },
  'Justice & Civic': { impactTags: ['Islah', 'Justice', 'Mizan', 'Amanah'], nameOfAllah: 'Al-Adl', ayatRef: '4:135', type: 'islah' },
  'Health': { impactTags: ['Khidmah', 'Health', 'Fisabilillah', 'Amanah'], nameOfAllah: 'Ash-Shafi', ayatRef: '26:80', type: 'service' },
  'Housing': { impactTags: ['Fisabilillah', 'Housing', 'Mizan', 'Amanah'], nameOfAllah: 'Al-Karim', ayatRef: '2:261', type: 'giving' },
  'Community Restoration': { impactTags: ['Islah', 'Khidmah', 'Fisabilillah', 'Amanah'], nameOfAllah: 'Al-Musawwir', ayatRef: '2:11', type: 'islah' },
  'Multi-Domain': { impactTags: ['Fisabilillah', 'Khidmah', 'Amanah', 'Mizan'], nameOfAllah: 'Al-Wadud', ayatRef: '76:8', type: 'service' },
};

const PARTNER_BADGE: Record<string, string> = {
  'CIOGC': 'CIOGC Partner',
  'Zakat Foundation': 'Zakat Foundation Partner',
  'MAS': 'MAS Partner',
  'Chi-Care': 'Chi-Care Partner',
  'MANA': 'MANA Partner',
  'ICNA': 'ICNA Relief Partner',
};

function mapPartnerEventToFeedItem(event: ExternalEvent): FeedItem {
  const domainConfig = PARTNER_DOMAIN_MAP[event.domain || 'Multi-Domain'] || PARTNER_DOMAIN_MAP['Multi-Domain'];
  return {
    id: event.id,
    type: domainConfig.type,
    title: event.title,
    description: event.description,
    city: event.city,
    domain: event.domain || 'Community',
    impact_tags: domainConfig.impactTags,
    contributor: event.partner,
    contributor_role: 'community-caretaker',
    contributor_badge: PARTNER_BADGE[event.partner],
    timestamp: event.date || new Date().toISOString(),
    source: event.source,
    source_url: event.url,
    name_of_allah: domainConfig.nameOfAllah,
    ayat_ref: domainConfig.ayatRef,
    reaction_count: 0,
    is_verified: true,
    partner_network: event.partner,
  };
}

// ─── CURATED FEED — Verified Asbab Quest challenges from partner orgs ─────────
const CURATED_FEED: FeedItem[] = [
  {
    id: 'f1',
    type: 'khidmah',
    title: 'Asbab Quest ◈ Chi-Care Community Fridge Caretaker — Stock & Document',
    description: 'Chi-Care operates community fridges across Chicago providing 24/7 free food access in underserved neighborhoods. Quest: Stock a Chi-Care community fridge, photograph the provisions as Ayat of Ar-Razzaq; The Ever-Provider, and submit an IRL Lens observation linking the act to Quran 2:177. Source: chi-care.org | Verified program.',
    city: 'Chicago',
    ward: 'South Side',
    domain: 'Food Security',
    impact_tags: ['Khidmah', 'Fisabilillah', 'Food', 'Amanah'],
    contributor: 'Chi-Care',
    contributor_role: 'community-caretaker',
    contributor_badge: 'Chi-Care Partner',
    timestamp: '2026-03-17T10:00:00Z',
    source: 'Chi-Care',
    source_url: 'https://chi-care.org',
    name_of_allah: 'Ar-Razzaq',
    ayat_ref: '2:177',
    reaction_count: 47,
    is_verified: true,
    partner_network: 'Chi-Care',
  },
  {
    id: 'f2',
    type: 'islah',
    title: 'Asbab Quest ◈ MANA Justice Initiative — Advocate for Black Muslim Communities',
    description: 'The Muslim Alliance in North America (MANA) works for justice and equity for Black Muslim communities through advocacy, organizing, and community building. Quest: Engage with MANA\'s justice campaigns, document the Ayat of Al-Adl; The Just in your community context, and share your reflection. Source: manajustice.org | Verified organization.',
    city: 'National',
    ward: 'Community',
    domain: 'Justice & Reentry',
    impact_tags: ['Islah', 'Justice', 'Reentry', 'Mizan'],
    contributor: 'MANA',
    contributor_role: 'activist',
    contributor_badge: 'MANA Partner',
    timestamp: '2026-03-28T13:00:00Z',
    source: 'MANA',
    source_url: 'https://www.manajustice.org',
    name_of_allah: 'Al-Adl',
    ayat_ref: '57:25',
    reaction_count: 89,
    is_verified: true,
    partner_network: 'MANA',
  },
  {
    id: 'f3',
    type: 'islah',
    title: 'Asbab Quest ◈ CIOGC Civic Engagement — Voter Registration & Advocacy',
    description: 'The Council of Islamic Organizations of Greater Chicago (CIOGC) runs civic engagement initiatives including voter registration, candidate forums, and community advocacy. Quest: Register 5 community members to vote or attend a CIOGC civic forum. Map the civic engagement network as Ayat of Al-Adl in your community. Source: ciogc.org | Verified annual program.',
    city: 'Chicago',
    domain: 'Justice & Civic',
    impact_tags: ['Islah', 'Justice', 'Mizan', 'Amanah'],
    contributor: 'CIOGC',
    contributor_role: 'activist',
    contributor_badge: 'CIOGC Partner',
    timestamp: '2026-03-20T09:00:00Z',
    source: 'CIOGC',
    source_url: 'https://www.ciogc.org',
    name_of_allah: 'Al-Adl',
    ayat_ref: '4:135',
    reaction_count: 203,
    is_verified: true,
    partner_network: 'CIOGC',
  },
  {
    id: 'f4',
    type: 'giving',
    title: 'Asbab Quest ◈ Zakat Foundation — Sponsor a Ramadan Food Basket',
    description: 'Zakat Foundation Foundation America distributes Ramadan food baskets to thousands of low-income families. Quest: Sponsor one food basket or volunteer at a local distribution site. Reflect on Quran 2:177 — "Righteousness is to give wealth, in spite of love for it, to relatives, orphans, the needy, the traveler..." Source: zakatfoundation.org | Verified 501(c)(3).',
    city: 'National',
    domain: 'Food Security',
    impact_tags: ['Fisabilillah', 'Food', 'Amanah', 'Khidmah'],
    contributor: 'Zakat Foundation',
    contributor_role: 'community-caretaker',
    contributor_badge: 'Zakat Foundation Partner',
    timestamp: '2026-03-15T08:00:00Z',
    source: 'Zakat Foundation',
    source_url: 'https://www.zakatfoundation.org',
    name_of_allah: 'Ar-Razzaq',
    ayat_ref: '2:177',
    reaction_count: 156,
    is_verified: true,
    partner_network: 'Zakat Foundation',
  },
  {
    id: 'f5',
    type: 'service',
    title: 'Asbab Quest ◈ MAS Youth Tarbiyah — Mentor the Next Generation',
    description: 'Muslim American Society (MAS) runs youth tarbiyah (character development) programs and leadership training across chapters nationwide. Quest: Enroll a youth in MAS tarbiyah or volunteer as a mentor. Document how mentorship reflects Al-Alim; The All-Knowing\'s gift of knowledge. Source: muslimamericansociety.org | Verified national organization.',
    city: 'National',
    domain: 'Education',
    impact_tags: ['Khidmah', 'Education', 'Fisabilillah', 'Amanah'],
    contributor: 'MAS',
    contributor_role: 'community-caretaker',
    contributor_badge: 'MAS Partner',
    timestamp: '2026-03-22T14:00:00Z',
    source: 'MAS',
    source_url: 'https://www.muslimamericansociety.org',
    name_of_allah: 'Al-Alim',
    ayat_ref: '96:1',
    reaction_count: 74,
    is_verified: true,
    partner_network: 'MAS',
  },
  {
    id: 'f6',
    type: 'service',
    title: 'Asbab Quest ◈ ICNA Relief — Refugee Welcome & Resettlement Support',
    description: 'ICNA Relief\'s refugee resettlement program provides newly arrived refugees with housing, employment assistance, and community integration support. Quest: Volunteer as a cultural navigator, donate to the resettlement fund, or help a refugee family navigate their new community. Source: icnarelief.org | Verified 501(c)(3).',
    city: 'National',
    domain: 'Community Restoration',
    impact_tags: ['Islah', 'Khidmah', 'Fisabilillah', 'Amanah'],
    contributor: 'ICNA',
    contributor_role: 'community-caretaker',
    contributor_badge: 'ICNA Relief Partner',
    timestamp: '2026-04-02T10:00:00Z',
    source: 'ICNA',
    source_url: 'https://www.icnarelief.org',
    name_of_allah: 'Al-Wadud',
    ayat_ref: '76:8',
    reaction_count: 91,
    is_verified: true,
    partner_network: 'ICNA',
  },
];

const DOMAIN_FILTERS = [
  { id: 'all', label: 'All Domains' },
  { id: 'Food Security', label: 'Food' },
  { id: 'Justice & Reentry', label: 'Justice' },
  { id: 'Justice & Civic', label: 'Civic' },
  { id: 'Ecology', label: 'Ecology' },
  { id: 'Housing', label: 'Housing' },
  { id: 'Education', label: 'Education' },
  { id: 'Health', label: 'Health' },
  { id: 'Community Restoration', label: 'Restoration' },
];

const CITY_FILTERS = [
  { id: 'all', label: 'All Cities' },
  { id: 'Chicago', label: 'Chicago' },
  { id: 'National', label: 'National' },
  { id: 'Atlanta', label: 'Atlanta' },
  { id: 'Detroit', label: 'Detroit' },
];

const TYPE_FILTERS = [
  { id: 'all', label: 'All Acts' },
  { id: 'khidmah', label: 'Khidmah', icon: '⬡' },
  { id: 'islah', label: 'Islah', icon: '◈' },
  { id: 'service', label: 'Service', icon: '◎' },
  { id: 'giving', label: 'Giving', icon: '✦' },
  { id: 'caretaking', label: 'Caretaking', icon: '🌿' },
];

const TYPE_COLORS: Record<string, string> = {
  khidmah: '#2d8a87',
  islah: '#2d7a47',
  service: '#c9a84c',
  giving: '#D4A82A',
  caretaking: '#8a8ab8',
};

const ROLE_BADGE_COLORS: Record<string, string> = {
  'community-caretaker': '#2d7a47',
  'activist': '#2d8a87',
  'academic': '#D4A82A',
  'inner-seeker': '#8a8ab8',
};

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

function FeedCard({ item, onFollow }: { item: FeedItem; onFollow: (id: string) => void }) {
  const [followed, setFollowed] = useState(item.is_followed || false);
  const [reacted, setReacted] = useState(false);
  const [reactions, setReactions] = useState(item.reaction_count || 0);
  const typeColor = TYPE_COLORS[item.type] || '#c9a84c';
  const typeConfig = TYPE_FILTERS.find(t => t.id === item.type);

  function handleFollow() {
    setFollowed(f => !f);
    onFollow(item.id);
  }

  function handleReact() {
    if (!reacted) {
      setReactions(r => r + 1);
      setReacted(true);
    }
  }

  return (
    <div style={{
      background: 'rgba(6,13,18,0.7)',
      border: `1px solid rgba(255,255,255,0.07)`,
      borderLeft: `3px solid ${typeColor}`,
      borderRadius: 8,
      padding: '18px 20px',
      transition: 'border-color 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 8, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '2px 8px', borderRadius: 20,
            background: `${typeColor}15`, border: `1px solid ${typeColor}40`, color: typeColor,
          }}>
            {typeConfig?.icon} {item.type}
          </span>
          {item.city && (
            <span style={{
              fontSize: 8, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: 20,
              background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.25)', color: '#2d7a47',
            }}>
              📍 {item.city}{item.ward ? ` · ${item.ward}` : ''}
            </span>
          )}
          <span style={{ fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)' }}>
            {item.domain}
          </span>
          {item.is_verified && (
            <span style={{ fontSize: 9, color: '#2d7a47' }} title="Verified community contribution">✓</span>
          )}
        </div>
        <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', fontFamily: 'monospace', flexShrink: 0 }}>
          {timeAgo(item.timestamp)}
        </span>
      </div>

      <h3 style={{ color: '#F5F0E8', fontSize: 14, fontWeight: 600, lineHeight: 1.5, marginBottom: 8 }}>
        {item.title}
      </h3>

      <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.7, marginBottom: 12 }}>
        {item.description}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
        {item.impact_tags.map(tag => {
          const tc = IMPACT_TAG_COLORS[tag] || { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)', text: 'rgba(212,197,169,0.5)' };
          return (
            <span key={tag} style={{
              fontSize: 8, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: 20,
              background: tc.bg, border: `1px solid ${tc.border}`, color: tc.text,
            }}>
              {tag}
            </span>
          );
        })}
        {item.name_of_allah && (
          <span style={{
            fontSize: 8, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
            padding: '2px 8px', borderRadius: 20,
            background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c',
          }}>
            ✦ {item.name_of_allah}
          </span>
        )}
        {item.ayat_ref && (
          <span style={{
            fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em',
            padding: '2px 8px', borderRadius: 20,
            background: 'rgba(138,138,184,0.08)', border: '1px solid rgba(138,138,184,0.2)', color: '#8a8ab8',
          }}>
            {item.ayat_ref}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {item.contributor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: `${ROLE_BADGE_COLORS[item.contributor_role || ''] || '#c9a84c'}20`,
                border: `1px solid ${ROLE_BADGE_COLORS[item.contributor_role || ''] || '#c9a84c'}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, color: ROLE_BADGE_COLORS[item.contributor_role || ''] || '#c9a84c',
              }}>
                {item.contributor[0]}
              </div>
              <div>
                <span style={{ fontSize: 10, color: '#d4c5a9', fontFamily: 'monospace' }}>{item.contributor}</span>
                {item.contributor_badge && (
                  <span style={{
                    marginLeft: 6, fontSize: 7, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '1px 5px', borderRadius: 10,
                    background: `${ROLE_BADGE_COLORS[item.contributor_role || ''] || '#c9a84c'}12`,
                    border: `1px solid ${ROLE_BADGE_COLORS[item.contributor_role || ''] || '#c9a84c'}30`,
                    color: ROLE_BADGE_COLORS[item.contributor_role || ''] || '#c9a84c',
                  }}>
                    {item.contributor_badge}
                  </span>
                )}
              </div>
            </div>
          )}
          {item.source_url && (
            <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8, color: 'rgba(45,138,135,0.6)', fontFamily: 'monospace', textDecoration: 'none' }}>
              ↗ Source
            </a>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={handleReact}
            style={{
              background: reacted ? 'rgba(201,168,76,0.1)' : 'transparent',
              border: `1px solid ${reacted ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 20, padding: '3px 10px', cursor: 'pointer',
              fontSize: 9, fontFamily: 'monospace', letterSpacing: '.06em',
              color: reacted ? '#c9a84c' : 'rgba(212,197,169,0.4)',
              transition: 'all 0.15s',
            }}
          >
            الحمد لله {reactions > 0 && reactions}
          </button>
          <button
            onClick={handleFollow}
            style={{
              background: followed ? 'rgba(45,138,135,0.1)' : 'transparent',
              border: `1px solid ${followed ? 'rgba(45,138,135,0.4)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 20, padding: '3px 10px', cursor: 'pointer',
              fontSize: 9, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase',
              color: followed ? '#2d8a87' : 'rgba(212,197,169,0.4)',
              transition: 'all 0.15s',
            }}
          >
            {followed ? '✓ Following' : '+ Follow'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FeedSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ background: 'rgba(6,13,18,0.5)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8, padding: '18px 20px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div className="animate-pulse" style={{ width: 60, height: 16, background: 'rgba(212,197,169,0.06)', borderRadius: 20 }} />
            <div className="animate-pulse" style={{ width: 80, height: 16, background: 'rgba(212,197,169,0.06)', borderRadius: 20 }} />
          </div>
          <div className="animate-pulse" style={{ width: '85%', height: 18, background: 'rgba(212,197,169,0.06)', borderRadius: 4, marginBottom: 8 }} />
          <div className="animate-pulse" style={{ width: '100%', height: 14, background: 'rgba(212,197,169,0.04)', borderRadius: 4, marginBottom: 4 }} />
          <div className="animate-pulse" style={{ width: '70%', height: 14, background: 'rgba(212,197,169,0.04)', borderRadius: 4, marginBottom: 12 }} />
        </div>
      ))}
    </div>
  );
}

// ─── ANTHROPIC FEED REFRESH ───────────────────────────────────────────────────

async function fetchAIQuestChallenges(): Promise<FeedItem[]> {
  const prompt = `You are the Haal Project's Asbab Quest feed curator. Generate 4 fresh Asbab Quest challenges based on the known programs of these verified Islamic organizations:

1. CIOGC (Council of Islamic Organizations of Greater Chicago) - ciogc.org - civic engagement, interfaith, community programs
2. Zakat Foundation of America - zakatfoundation.org - zakat distribution, hunger relief, orphan sponsorship
3. MAS (Muslim American Society) - muslimamericansociety.org - youth programs, tarbiyah, community service
4. Chi-Care - chi-care.org - mobile meals, community fridges, food distribution in Chicago
5. MANA (Muslim Alliance in North America) - manajustice.org - justice advocacy, Black Muslim community support
6. ICNA Relief - icnarelief.org - disaster relief, refugee resettlement, food assistance

Each quest challenge must:
- Be framed as an Asbab Quest (means/causes toward good)
- Link to a Name of Allah (e.g., Ar-Razzaq, Al-Adl, Al-Khaliq)
- Include a Quranic reference (e.g., 2:177, 57:25)
- Have a concrete action step
- Use only verified programs from the org's actual website

Return a JSON array of exactly 4 objects with these fields:
{
  "title": "Asbab Quest ◈ [Org] — [Short Title]",
  "description": "Description with quest action, Name of Allah context, and Quranic reference. Source: [verified URL]",
  "org": "[Org name]",
  "domain": "[Food Security|Justice & Reentry|Education|Community Restoration|Justice & Civic|Health|Ecology]",
  "city": "[City or National]",
  "name_of_allah": "[Name]",
  "ayat_ref": "[Surah:Ayah]",
  "source_url": "[verified URL]",
  "type": "[khidmah|islah|service|giving|caretaking]",
  "impact_tags": ["tag1", "tag2", "tag3"]
}`;

  try {
    const response = await getChatCompletion(
      'ANTHROPIC',
      'claude-sonnet-4-5-20250929',
      [
        { role: 'system', content: 'You are a knowledgeable Islamic community resource curator. Return only valid JSON arrays, no markdown.' },
        { role: 'user', content: prompt }
      ],
      { temperature: 0.7, max_tokens: 2000 }
    );

    const content = response?.choices?.[0]?.message?.content || '';
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) return [];

    const parsed = JSON.parse(jsonMatch[0]);
    return parsed.map((item: any, idx: number): FeedItem => ({
      id: `ai-${Date.now()}-${idx}`,
      type: item.type || 'service',
      title: item.title,
      description: item.description,
      city: item.city,
      domain: item.domain || 'Multi-Domain',
      impact_tags: item.impact_tags || ['Fisabilillah', 'Amanah'],
      contributor: item.org,
      contributor_role: 'community-caretaker',
      contributor_badge: `${item.org} Partner`,
      timestamp: new Date().toISOString(),
      source: item.org,
      source_url: item.source_url,
      name_of_allah: item.name_of_allah,
      ayat_ref: item.ayat_ref,
      reaction_count: 0,
      is_verified: true,
      partner_network: item.org,
    }));
  } catch {
    return [];
  }
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function FisabilillahFeed() {
  const [filters, setFilters] = useState<FeedFilters>({ city: 'all', domain: 'all', type: 'all', sort: 'recent' });
  const [loading, setLoading] = useState(true);
  const [allItems, setAllItems] = useState<FeedItem[]>([]);
  const [followedIds, setFollowedIds] = useState<Set<string>>(new Set());
  const [aiRefreshing, setAiRefreshing] = useState(false);
  const [aiRefreshStatus, setAiRefreshStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [lastRefreshed, setLastRefreshed] = useState<string>('');

  useEffect(() => {
    // Initialize feed with curated + partner events
    const partnerMapped = PARTNER_EVENTS.map(mapPartnerEventToFeedItem);
    const combined = [...CURATED_FEED, ...partnerMapped];
    // Deduplicate by id
    const seen = new Set<string>();
    const deduped = combined.filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
    setAllItems(deduped);
    setLoading(false);
  }, []);

  const handleAIRefresh = useCallback(async () => {
    setAiRefreshing(true);
    setAiRefreshStatus('loading');
    try {
      const newItems = await fetchAIQuestChallenges();
      if (newItems.length > 0) {
        setAllItems(prev => {
          const existingIds = new Set(prev.map(i => i.id));
          const fresh = newItems.filter(i => !existingIds.has(i.id));
          return [...fresh, ...prev];
        });
        setAiRefreshStatus('done');
        setLastRefreshed(new Date().toLocaleTimeString());
      } else {
        setAiRefreshStatus('error');
      }
    } catch {
      setAiRefreshStatus('error');
    } finally {
      setAiRefreshing(false);
      setTimeout(() => setAiRefreshStatus('idle'), 4000);
    }
  }, []);

  const handleFollow = useCallback((id: string) => {
    setFollowedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const filteredItems = allItems.filter(item => {
    if (filters.city !== 'all' && item.city !== filters.city) return false;
    if (filters.domain !== 'all' && item.domain !== filters.domain) return false;
    if (filters.type !== 'all' && item.type !== filters.type) return false;
    return true;
  }).sort((a, b) => {
    if (filters.sort === 'impact') return (b.reaction_count || 0) - (a.reaction_count || 0);
    if (filters.sort === 'city') return (a.city || '').localeCompare(b.city || '');
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });

  return (
    <section id="fisabilillah-feed" style={{ background: '#060d12', padding: '80px 0' }}>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 12 }}>
            فِي سَبِيلِ اللَّه · IN THE PATH OF ALLAH
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(16px, 3vw, 24px)', color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 12 }}>
            FISABILILLAH FEED
          </h2>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 15, fontStyle: 'italic', maxWidth: 560, margin: '0 auto 20px' }}>
            Verified Asbab Quest challenges from CIOGC, Zakat Foundation, MAS, Chi-Care, MANA, and ICNA — structured as caretaking actions linked to Names of Allah and Quranic guidance.
          </p>

          {/* Org source badges */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
            {[
              { name: 'CIOGC', url: 'https://www.ciogc.org', color: '#c9a84c' },
              { name: 'Zakat Foundation', url: 'https://www.zakatfoundation.org', color: '#2d7a47' },
              { name: 'MAS', url: 'https://www.muslimamericansociety.org', color: '#2d8a87' },
              { name: 'Chi-Care', url: 'https://chi-care.org', color: '#D4A82A' },
              { name: 'MANA', url: 'https://www.manajustice.org', color: '#8a8ab8' },
              { name: 'ICNA Relief', url: 'https://www.icnarelief.org', color: '#c05050' },
            ].map(org => (
              <a key={org.name} href={org.url} target="_blank" rel="noopener noreferrer" style={{
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
                padding: '3px 10px', borderRadius: 20, textDecoration: 'none',
                background: `${org.color}10`, border: `1px solid ${org.color}30`, color: org.color,
                transition: 'all 0.15s',
              }}>
                ↗ {org.name}
              </a>
            ))}
          </div>

          {/* AI Refresh Button */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <button
              onClick={handleAIRefresh}
              disabled={aiRefreshing}
              style={{
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                padding: '6px 16px', borderRadius: 20, cursor: aiRefreshing ? 'wait' : 'pointer',
                background: aiRefreshStatus === 'done' ? 'rgba(45,122,71,0.15)' : aiRefreshStatus === 'error' ? 'rgba(180,80,80,0.1)' : 'rgba(201,168,76,0.08)',
                border: `1px solid ${aiRefreshStatus === 'done' ? 'rgba(45,122,71,0.4)' : aiRefreshStatus === 'error' ? 'rgba(180,80,80,0.3)' : 'rgba(201,168,76,0.25)'}`,
                color: aiRefreshStatus === 'done' ? '#2d7a47' : aiRefreshStatus === 'error' ? '#c05050' : '#c9a84c',
                transition: 'all 0.2s',
              }}
            >
              {aiRefreshing ? '⟳ Generating Quests...' : aiRefreshStatus === 'done' ? '✓ Feed Refreshed' : aiRefreshStatus === 'error' ? '⚠ Retry Refresh' : '⟳ AI Refresh Feed'}
            </button>
            {lastRefreshed && (
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>
                Last: {lastRefreshed}
              </span>
            )}
          </div>
          <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.25)', marginTop: 8 }}>
            Uses Anthropic Claude to generate fresh quest challenges from verified org programs
          </p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {DOMAIN_FILTERS.slice(0, 5).map(f => (
              <button key={f.id} onClick={() => setFilters(prev => ({ ...prev, domain: f.id }))} style={{
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase',
                padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                background: filters.domain === f.id ? 'rgba(201,168,76,0.12)' : 'transparent',
                border: `1px solid ${filters.domain === f.id ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`,
                color: filters.domain === f.id ? '#c9a84c' : 'rgba(212,197,169,0.4)',
              }}>
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginLeft: 'auto' }}>
            {(['recent', 'impact'] as const).map(s => (
              <button key={s} onClick={() => setFilters(prev => ({ ...prev, sort: s }))} style={{
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase',
                padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                background: filters.sort === s ? 'rgba(45,138,135,0.12)' : 'transparent',
                border: `1px solid ${filters.sort === s ? 'rgba(45,138,135,0.4)' : 'rgba(255,255,255,0.08)'}`,
                color: filters.sort === s ? '#2d8a87' : 'rgba(212,197,169,0.4)',
              }}>
                {s === 'recent' ? '↓ Recent' : '↑ Impact'}
              </button>
            ))}
          </div>
        </div>

        {/* Feed items */}
        {loading ? (
          <FeedSkeleton />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filteredItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', color: 'rgba(212,197,169,0.4)' }}>
                <p style={{ fontFamily: 'monospace', fontSize: 11 }}>No quests match these filters.</p>
              </div>
            ) : (
              filteredItems.map(item => (
                <FeedCard key={item.id} item={{ ...item, is_followed: followedIds.has(item.id) }} onFollow={handleFollow} />
              ))
            )}
          </div>
        )}

        {/* Footer note */}
        <div style={{ marginTop: 32, padding: '16px 20px', borderRadius: 8, background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', lineHeight: 1.7 }}>
            All feed items are verified Asbab Quest challenges sourced from official org websites. Source links open verified org pages. Use the AI Refresh button to generate fresh challenges via Anthropic Claude. To submit a community observation or quest, visit the{' '}
            <Link href="/inner-reality-lens" style={{ color: 'rgba(45,138,135,0.7)' }}>IRL Lens</Link>{' '}or{' '}
            <Link href="/asbab-quest" style={{ color: 'rgba(201,168,76,0.7)' }}>Asbab Quest</Link> pages.
          </p>
        </div>
      </div>
    </section>
  );
}
